/**
 * Query hooks for communication gateways, channel-aware templates and chat
 * (boards #233 and #232).
 *
 * Page-local, following `useInAppNotifications.ts` (board #231): the shared
 * `hooks/useCommunications.ts` is typed against `@/types`'
 * `NotificationTemplate` / `NotificationChannel`, which describe an API that
 * does not exist. Its `useTemplates` / `useCreateTemplate` / `useChannels`
 * have no callers left once TemplatesPage stops using them.
 *
 * Everything here reports failures rather than smoothing them over. In
 * particular `useTestGateway` resolves with `{ok: false, error}` when the
 * gateway could not send, and the caller renders that error verbatim -- it
 * never shows a success toast for an attempt that failed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { communicationService, getErrorMessage } from '@/services';
import type {
  CommunicationTemplateFilters,
  CommunicationTemplateInput,
  GatewayInput,
  SendEmailInput,
} from '@/services/communications';

const GATEWAYS = ['communication-gateways'] as const;

export function useGateways() {
  return useQuery({
    queryKey: GATEWAYS,
    queryFn: () => communicationService.listGateways(),
  });
}

export function useGateway(id: string | null) {
  return useQuery({
    queryKey: ['communication-gateway', id],
    queryFn: () => communicationService.getGateway(id as string),
    enabled: !!id,
  });
}

export function useProviderCatalogue() {
  return useQuery({
    queryKey: ['communication-providers'],
    queryFn: () => communicationService.getProviderCatalogue(),
    // The catalogue is a static table on the server.
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateGateway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: GatewayInput) => communicationService.createGateway(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GATEWAYS });
      toast.success('Gateway saved');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useUpdateGateway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<GatewayInput> }) =>
      communicationService.updateGateway(id, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: GATEWAYS });
      queryClient.invalidateQueries({ queryKey: ['communication-gateway', variables.id] });
      toast.success('Gateway updated');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useDeleteGateway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationService.deleteGateway(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GATEWAYS });
      toast.success('Gateway deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Sends a real test message. The result carries `ok`, and `ok: false` is the
 * expected outcome today -- no provider adapter is implemented, so the server
 * answers 503 with the reason. Deliberately no success toast here: the caller
 * decides what to say once it has read `ok`.
 */
export function useTestGateway() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, recipient }: { id: string; recipient?: string }) =>
      communicationService.testGateway(id, recipient),
    onSuccess: () => {
      // A test writes a Communication row either way, so the counters move.
      queryClient.invalidateQueries({ queryKey: GATEWAYS });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
export function useCommunicationTemplates(page: number, filters?: CommunicationTemplateFilters) {
  return useQuery({
    queryKey: ['communication-templates', page, filters],
    queryFn: () => communicationService.getTemplates(page, filters),
    placeholderData: (previous) => previous,
  });
}

export function useTemplateSchema() {
  return useQuery({
    queryKey: ['communication-template-schema'],
    queryFn: () => communicationService.getTemplateSchema(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateCommunicationTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CommunicationTemplateInput) =>
      communicationService.createCommunicationTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communication-templates'] });
      toast.success('Template created');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/**
 * Compose and send one email (#114).
 *
 * DELIBERATELY NO SUCCESS TOAST. A 201 here means the attempt was recorded,
 * not that the mail arrived; the caller reads `status` off the returned
 * delivery record and shows what really happened. A toast fired on 201 would
 * be this platform's signature bug -- a green tick with nothing behind it.
 */
export function useSendEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SendEmailInput) => communicationService.sendEmail(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communication-threads'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

// ---------------------------------------------------------------------------
// Chat / conversations (#232)
// ---------------------------------------------------------------------------
export function useMessageThreads(channelType: string | null) {
  return useQuery({
    queryKey: ['communication-threads', channelType],
    queryFn: () => communicationService.getMessageThreads(channelType as string),
    enabled: !!channelType,
  });
}

export function useChatRooms(enabled: boolean) {
  return useQuery({
    queryKey: ['chat-rooms'],
    queryFn: () => communicationService.getChatRooms(),
    enabled,
  });
}

export function useRoomMessages(roomId: string | null) {
  return useQuery({
    queryKey: ['chat-room-messages', roomId],
    queryFn: () => communicationService.getChatMessages(roomId as string),
    enabled: !!roomId,
    refetchInterval: 15000,
  });
}

export function useSendRoomMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roomId, content }: { roomId: string; content: string }) =>
      communicationService.sendChatMessage(roomId, content),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['chat-room-messages', variables.roomId] });
      queryClient.invalidateQueries({ queryKey: ['chat-rooms'] });
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}
